import { App } from "@capacitor/app";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import socketService from "../services/socket.service";
import { useUserStore } from "../store/useUserStore";

/**
 * Headless component that manages the global socket connection
 * and reports the appState (foreground/background) to the backend.
 */
export const GlobalSocketHandler: React.FC = () => {
  const { profile } = useUserStore();
  const location = useLocation();

  useEffect(() => {
    // Only connect if we have a profile ID (meaning user is likely logged in and initialized)
    if (!profile?._id) {
      socketService.disconnect();
      return;
    }

    const socket = socketService.connect();
    if (!socket) return;

    // Report initial state
    socket.emit("appState", { inForeground: true });

    // Listen for app state changes (background/foreground)
    const appStateListener = App.addListener("appStateChange", ({ isActive }) => {
      console.log(`Global App state changed: inForeground=${isActive}`);
      socket.emit("appState", { inForeground: isActive });
    });

    return () => {
      appStateListener.then((listener) => listener.remove());
    };
  }, [profile?._id]);

  // Report navigation changes to the backend
  useEffect(() => {
    const socket = socketService.getSocket();
    if (socket?.connected) {
      console.log(`Reporting navigation to: ${location.pathname}`);
      socket.emit("navigation", { path: location.pathname });
    }
  }, [location.pathname, profile?._id]);

  return null;
};
